import qs from 'qs'
import { useDispatch, useSelector } from 'react-redux'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import useTranslation from '@/hooks/useTranslation'
import { generalConstants } from '@tweddle-se-team/common/constants'
import { useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { productActions } from '@/store/products'
import { searchActions } from '@/store/search'
import { showSuccess } from '@/components/shared/toaster'
import TMEVinSearch from './defaultVinSearch'
import SubaruVinSearch from '@/components/themes/subaru/landing/vinSearch'
import { configSelectors } from '@/store/configs'
import { customerHelper } from '@tweddle-se-team/common/constants'
import { decodeVinSubaru } from '../themes/subaru/landing/decodeVin'
import { decodeVinNordics } from '../themes/subaru-nordics/landing/decodeVin'
import CustomerNotFound from '@/components/shared/customerNotFound'

const { CUSTOMER_IDS } = customerHelper

const VinComponentMapper = {
  [CUSTOMER_IDS.SUBARU]: SubaruVinSearch,
  [CUSTOMER_IDS.TME]: TMEVinSearch,
  [CUSTOMER_IDS.NORDICS]: SubaruVinSearch
}

const showActionIconByCustomer = {
  [CUSTOMER_IDS.SUBARU]: () => false,
  [CUSTOMER_IDS.TME]: brand => brand.toLowerCase() === BRANDS.LEXUS,
  [CUSTOMER_IDS.NORDICS]: () => true
}

const { BRANDS } = generalConstants

export const decodeVinTME = async ({
  setBrand,
  vin,
  setVin,
  setLoadingVIN,
  setVINError,
  products,
  i18nL10n,
  t,
  router
}) => {
  try {
    const { locale } = i18nL10n

    setVin(vin)
    setVINError(false)
    setLoadingVIN(true)
    let findCriteriaByVinUrl = `publications/findCriteriaByVin/${vin}`
    if (products?.filterBy?.brand) {
      findCriteriaByVinUrl += `/${products.filterBy.brand.toUpperCase()}`
    }

    const result = await apiWrapper.get(findCriteriaByVinUrl) // Uncomment this line for actual API call // Mock API call for testing, to be removed later
    if (!result.success) setVINError(result.response.message)
    else {
      const {
        brand,
        model,
        modelType,
        ngtdModelId,
        katashiki,
        productionDate,
        dateFrom,
        dateTo,
        generationId,
        availableLanguagesCodes
      } = result.response
      const query = {
        brand,
        model,
        modelType,
        ngtdModelId,
        katashiki,
        productionDate,
        dateFrom,
        dateTo,
        generationId,
        hideFooter: true
      }
      setBrand(brand)

      if (locale === 'uk' && !availableLanguagesCodes.includes(locale)) {
        return setVINError(t('unable_to_find_publications'))
      }

      showSuccess(t('vin_recognized'))

      if (locale && availableLanguagesCodes.includes(locale)) {
        query.language = locale
        router.push(`/publications?${qs.stringify(query)}`)
      } else {
        router.push(`/languages?${qs.stringify(query)}`)
      }
    }
  } catch (e) {
    if (e.response?.status === 400) {
      setVINError(t('vin_incorrect_format'))
    } else if (e.response?.status === 429) setVINError(t('rate_limit_exceeded'))
    else {
      console.error(e)
      setVINError(t('vin_decode_unavailable'))
    }
  } finally {
    setLoadingVIN(false)
  }
}

const DecoderMapper = {
  [CUSTOMER_IDS.SUBARU]: decodeVinSubaru,
  [CUSTOMER_IDS.TME]: decodeVinTME,
  [CUSTOMER_IDS.NORDICS]: decodeVinNordics
}

const VinSearch = ({ i18nL10n, products }) => {
  const dispatch = useDispatch()
  const { customerId } = useSelector(configSelectors.selectConfigs)
  const t = useTranslation()
  const [vin, setVin] = useState('')
  const [showVINInfo, setShowVINInfo] = useState(false)
  const [loadingVIN, setLoadingVIN] = useState(false)
  const [VINError, setVINError] = useState(false)
  const router = useRouter()
  const {
    filterBy: { brand }
  } = products

  const showActionIcon = (showActionIconByCustomer[customerId] || showActionIconByCustomer[CUSTOMER_IDS.TME])(brand)

  const setBrand = useCallback(
    brand => {
      dispatch(productActions.SET_BRAND(brand))
      dispatch(searchActions.SET_BRAND(brand))
    },
    [dispatch]
  )

  const decodeVinFn = useCallback(() => {
    if (!DecoderMapper[customerId]) {
      console.error('No decodeVin function found for customerId ' + customerId)
      return
    }
    return DecoderMapper[customerId]({
      setBrand,
      vin,
      setVin,
      setLoadingVIN,
      setVINError,
      products,
      i18nL10n,
      t,
      router
    })
  }, [customerId, setBrand, vin, setVin, setLoadingVIN, setVINError, products, i18nL10n, t, router])

  const BrandedVinSearch = VinComponentMapper[customerId] || CustomerNotFound

  return (
    <BrandedVinSearch
      showVINInfo={showVINInfo}
      setShowVINInfo={setShowVINInfo}
      vin={vin}
      setVin={setVin}
      setVINError={setVINError}
      loadingVIN={loadingVIN}
      VINError={VINError}
      decodeVinFn={decodeVinFn}
      showActionIcon={showActionIcon}
    />
  )
}

export default VinSearch
